/**
 * Tiny path router for /dashboard (history API; the Worker serves the shell for every /dashboard path).
 *
 * Navigation never reloads the page. Most pages paint from snapshots the Worker inlines into the HTML it serves for
 * that path (main.tsx), so before a navigation commits, a preparer (setPreparer, main.tsx) may fetch the next page's
 * snapshots while the current page stays on screen; the URL and the route change together once they've arrived.
 * Same-page changes (a tab in the query string or the path) commit at once.
 */
import { useSyncExternalStore } from "react";
import { scrollToTop, scrollTop, viewTop } from "./scroller";

export interface Route {
  path: string;                 // location.pathname
  section: string;              // "overview" | "meetings" | "people" | ... (second path segment)
  meetingId: string | null;     // /dashboard/meetings/<id>
  tab: string | null;           // /dashboard/meetings/<id>/<tab>
  personId?: string | null;     // /dashboard/people/<id>[/<tab>] (People section)
  publicMeeting?: boolean;      // /meeting/<id>[/<tab>]: the public meeting page
}

export function parse(path: string): Route {
  // The public meeting page (/meeting/<id>[/<tab>]): the same meeting page outside the dashboard (main.tsx).
  const pub = /^\/meeting\/([^/]+)(?:\/([^/]+))?\/?$/.exec(path);
  if (pub) return { path, section: "meetings", meetingId: decodeURIComponent(pub[1]), tab: pub[2] ?? null, personId: null, publicMeeting: true };
  const parts = path.replace(/^\/dashboard\/?/, "").split("/").filter(Boolean);
  const section = parts[0] ?? "overview";
  const meetingId = section === "meetings" && parts[1] ? decodeURIComponent(parts[1]) : null;
  const personId = section === "people" && parts[1] ? decodeURIComponent(parts[1]) : null;
  return { path, section, meetingId, tab: meetingId || personId ? parts[2] ?? null : null, personId };
}

/** Runs before a navigation to `url` commits; resolves once the page can render from its data. Throwing falls back to a full page load. */
export type Preparer = (url: URL, opts: { force: boolean }) => Promise<void> | void;
let preparer: Preparer | null = null;
export const setPreparer = (p: Preparer) => { preparer = p; };

// What the app renders: changes only when a navigation commits (not when the address bar changes first, as on Back).
const browser = typeof window !== "undefined";   // false when a test renders a page on the server
let current = browser ? location.pathname + location.search : "";
let version = 0;   // bumped by reloadPage() so the page remounts with fresh data
let pending = 0;   // navigations waiting on their data (the progress bar)
let seq = 0;       // the latest navigation; an older one that finishes late is dropped
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (browser && "scrollRestoration" in history) history.scrollRestoration = "manual";
const saveScroll = () => { try { history.replaceState({ ...(history.state ?? {}), scroll: scrollTop() }, ""); } catch { /* ignore */ } };
let scrollTimer: ReturnType<typeof setTimeout> | undefined;
// Captured: the page may scroll inside the panel (app/scroller.ts), whose scroll events don't reach the window.
if (browser) addEventListener("scroll", () => { clearTimeout(scrollTimer); scrollTimer = setTimeout(saveScroll, 150); }, { passive: true, capture: true });

const pathOnly = (u: string) => u.split(/[?#]/)[0].replace(/(.)\/+$/, "$1");

function scrollAfterCommit(restore: number | null) {
  // After React has painted the new page.
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (restore != null) { scrollToTop(restore); return; }
    const id = location.hash.slice(1);
    const el = id ? document.getElementById(decodeURIComponent(id)) : null;
    if (el) scrollToTop(el.getBoundingClientRect().top - viewTop() + scrollTop()); else scrollToTop(0);
  }));
}

async function go(url: URL, { replace, pop, force }: { replace: boolean; pop: boolean; force: boolean }) {
  const mine = ++seq;
  const next = url.pathname + url.search;
  const samePage = pageKeyOf(next) === pageKeyOf(current) && !force;
  if (!samePage && preparer) {
    pending++; notify();
    try {
      await preparer(url, { force });
    } catch {
      // Couldn't load it in place (a system page, a different session): load it as a page, keeping this one in history.
      if (mine === seq) { if (pop || replace) location.replace(url.href); else location.assign(url.href); }
      return;
    } finally {
      pending--;
    }
    if (mine !== seq) { notify(); return; }
  }
  if (!pop) {
    const href = url.pathname + url.search + url.hash;
    if (replace) history.replaceState(history.state, "", href);
    else if (href !== location.pathname + location.search + location.hash) { saveScroll(); history.pushState(null, "", href); }
  }
  // A new query on the same page that isn't a tab switch (a saved view, Back between filters): pages read their
  // filters when they mount, so remount it. Tab switches use { replace } and keep the page as it is.
  if (force || (samePage && next !== current && (!replace || pop))) version++;
  current = next;
  notify();
  if (!samePage) scrollAfterCommit(pop ? (history.state?.scroll ?? 0) : null);
  else if (url.hash && !pop) scrollAfterCommit(null);
}

export function navigate(path: string, { replace = false } = {}) {
  const url = new URL(path, location.href);
  // Another site, or no app mounted to render it (a system page): a normal page load.
  if (url.origin !== location.origin || !listeners.size) { location.assign(url.href); return; }
  if (url.pathname + url.search + url.hash === location.pathname + location.search + location.hash && url.pathname + url.search === current) return;
  void go(url, { replace, pop: false, force: false });
}

/** Fetch the current page's data again and redraw it (instead of location.reload()). */
export function reloadPage() {
  void go(new URL(location.href), { replace: true, pop: true, force: true });
}

if (browser) addEventListener("popstate", () => {
  // Only the #fragment changed (an in-page link): the browser scrolls; nothing to render.
  if (location.pathname + location.search === current) return;
  void go(new URL(location.href), { replace: false, pop: true, force: false });
});

/** Same-origin links into the dashboard navigate in place; new-tab clicks, downloads and other pages load normally. */
const FULL_LOAD = /^\/dashboard\/(signin|signup|invite)(\/|$)|^\/dashboard\/assets\//;
export function interceptLinks() {
  addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
    if (!a || (a.target && a.target !== "_self") || a.hasAttribute("download")) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin || !/^\/dashboard(\/|$)/.test(url.pathname) || FULL_LOAD.test(url.pathname)) return;
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return; // in-page anchor
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  });
}

/**
 * Last resort for a page whose data isn't loaded: load the URL as a page, once. If the server still has nothing for
 * it, render the empty state rather than reloading in a loop.
 */
export function loadAsPage(): null {
  const k = `loaded-as-page:${location.pathname}${location.search}`;
  try { if (sessionStorage.getItem(k)) return null; sessionStorage.setItem(k, "1"); setTimeout(() => sessionStorage.removeItem(k), 10_000); } catch { /* storage off: still load once */ }
  location.replace(location.href);
  return null;
}

/** For click handlers on <a>: leave modified clicks (new tab/window) to the browser. */
export const plainClick = (e: { button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean }) =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

/** One key per page whose data is loaded together: a meeting's or a person's tabs share their page. */
export function pageKeyOf(pathname: string) {
  const p = pathOnly(pathname);
  const r = parse(p);
  if (r.meetingId) return `/dashboard/meetings/${encodeURIComponent(r.meetingId)}`;
  if (r.personId) return `/dashboard/people/${encodeURIComponent(r.personId)}`;
  return p;
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const pathOf = () => current.split("?")[0];
export const useRoute = () => parse(useSyncExternalStore(subscribe, pathOf, pathOf));
/** Path + query of the page on screen: pages that read ?tab= / ?q= re-read it when only the query changes. */
export const useLocationKey = () => useSyncExternalStore(subscribe, () => current, () => current);
/** Bumped by reloadPage(); used as a key so the page remounts with its fresh data. */
export const usePageVersion = () => useSyncExternalStore(subscribe, () => version, () => version);
export const useNavigating = () => useSyncExternalStore(subscribe, () => pending > 0, () => false);
